import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Timeline as SprintIcon,
  Sync as SyncIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { authGet, authPost, authPut, authDelete } from '../../services/apiService';
import { isAdmin } from '../../services/authService';
import sprintSyncStatusService from '../../services/sprintSyncStatusService';

/**
 * Sprint Management component for admin panel
 * @returns {JSX.Element} Sprint Management component
 */
const SprintManagement = () => {
  // State management
  const [teams, setTeams] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncingSprintId, setSyncingSprintId] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sprintStatuses, setSprintStatuses] = useState({}); // Map of sprintName -> { sprintClosed }
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [closingSprintId, setClosingSprintId] = useState(null);
  const [scopeChangeCalculationDays, setScopeChangeCalculationDays] = useState(0);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newSprintKpiEnabled, setNewSprintKpiEnabled] = useState(false);

  // Dialog states
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [sprintDialogOpen, setSprintDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [editingSprint, setEditingSprint] = useState(null);

  // Form states
  const [teamForm, setTeamForm] = useState({
    id: '',
    displayName: '',
    description: ''
  });
  const [sprintForm, setSprintForm] = useState({
    sprintName: '',
    startDate: '',
    endDate: '',
    quarter: '',
    year: ''
  });

  // Load data on component mount
  useEffect(() => {
    loadTeams();
    loadSprints();
    loadSprintConfig();
  }, []);

  // Load sprint configuration
  const loadSprintConfig = async () => {
    try {
      const data = await authGet('/api/admin/sprint-config');
      if (data.success) {
        setScopeChangeCalculationDays(data.data.scopeChangeCalculationDays ?? 0);
        setNewSprintKpiEnabled(!!data.data.newSprintKpiEnabled);
      }
    } catch (err) {
      console.error('Error loading sprint config:', err);
    }
  };

  // Save sprint configuration
  const handleSaveConfig = async () => {
    try {
      setSavingConfig(true);
      setError(null);
      
      const data = await authPut('/api/admin/sprint-config', {
        scopeChangeCalculationDays: scopeChangeCalculationDays,
        newSprintKpiEnabled
      });
      
      if (data.success) {
        setSuccess('Sprint configuration saved successfully');
      } else {
        setError(data.message || 'Failed to save sprint configuration');
      }
    } catch (err) {
      console.error('Error saving sprint config:', err);
      setError(err.message || 'Failed to save sprint configuration');
    } finally {
      setSavingConfig(false);
    }
  };

  // Load teams
  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await authGet('/api/sprint-report/teams');
      
      if (data.success) {
        setTeams(data.data);
      } else {
        setError('Failed to load teams');
      }
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  // Load sprints
  const loadSprints = async () => {
    try {
      const data = await authGet('/api/admin/sprints');
      
      if (data.success) {
        setSprints(data.data);
        
        // Load sprint statuses for each sprint
        const statusMap = {};
        for (const sprint of data.data) {
          try {
            // Try to get sprint data from any team to check status
            const teamsData = await authGet('/api/sprint-report/teams');
            if (teamsData.success && teamsData.data.length > 0) {
              const firstTeam = teamsData.data[0];
              const sprintData = await authGet(`/api/sprint-report/sprint-data/${sprint.sprintName}/${firstTeam.name}`);
              if (sprintData.success && sprintData.data) {
                statusMap[sprint.sprintName] = {
                  sprintClosed: sprintData.data.sprintClosed || false
                };
              }
            }
          } catch (err) {
            // If sprint data doesn't exist yet, set defaults
            statusMap[sprint.sprintName] = {
              sprintClosed: false
            };
          }
        }
        setSprintStatuses(statusMap);
      } else {
        setError('Failed to load sprints');
      }
    } catch (err) {
      console.error('Error loading sprints:', err);
      setError('Failed to load sprints');
    }
  };

  // Handle team form submission
  const handleTeamSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      let data;
      if (editingTeam) {
        data = await authPut(`/api/admin/teams/${editingTeam.id}`, teamForm);
      } else {
        data = await authPost('/api/admin/teams', teamForm);
      }

      if (data.success) {
        setSuccess(editingTeam ? 'Team updated successfully' : 'Team created successfully');
        setTeamDialogOpen(false);
        setEditingTeam(null);
        setTeamForm({ id: '', displayName: '', description: '' });
        loadTeams();
      } else {
        setError(data.message || 'Failed to save team');
      }
    } catch (err) {
      console.error('Error saving team:', err);
      setError('Failed to save team');
    } finally {
      setLoading(false);
    }
  };

  // Handle sprint form submission
  const handleSprintSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build sprint data without team field
      const sprintData = {
        sprintName: sprintForm.sprintName,
        startDate: sprintForm.startDate,
        endDate: sprintForm.endDate,
        quarter: sprintForm.quarter || getQuarterFromDate(sprintForm.startDate),
        year: sprintForm.year || new Date(sprintForm.startDate).getFullYear().toString()
      };

      console.log('Submitting sprint data:', sprintData);

      let data;
      if (editingSprint) {
        data = await authPut(`/api/admin/sprints/${editingSprint.sprintName}`, sprintData);
      } else {
        data = await authPost('/api/admin/sprints', sprintData);
      }

      console.log('Server response:', data);

      if (data.success) {
        setSuccess(editingSprint ? 'Sprint updated successfully' : 'Sprint created successfully');
        setSprintDialogOpen(false);
        setEditingSprint(null);
        setSprintForm({ sprintName: '', startDate: '', endDate: '', quarter: '', year: '' });
        loadSprints();
      } else {
        setError(data.message || 'Failed to save sprint');
      }
    } catch (err) {
      console.error('Error saving sprint:', err);
      setError(`Failed to save sprint: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get quarter from date
  const getQuarterFromDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1; // getMonth() returns 0-11
    
    if (month >= 1 && month <= 3) return 'Q1';
    if (month >= 4 && month <= 6) return 'Q2';
    if (month >= 7 && month <= 9) return 'Q3';
    if (month >= 10 && month <= 12) return 'Q4';
    
    return 'Q1';
  };

  // Handle team edit
  const handleTeamEdit = (team) => {
    setEditingTeam(team);
    setTeamForm({
      id: team.id,
      displayName: team.displayName,
      description: team.description || ''
    });
    setTeamDialogOpen(true);
  };

  // Handle sprint edit
  const handleSprintEdit = (sprint) => {
    setEditingSprint(sprint);
    setSprintForm({
      sprintName: sprint.sprintName,
      startDate: sprint.startDate ? new Date(sprint.startDate).toISOString().split('T')[0] : '',
      endDate: sprint.endDate ? new Date(sprint.endDate).toISOString().split('T')[0] : '',
      quarter: sprint.quarter || '',
      year: sprint.year || ''
    });
    setSprintDialogOpen(true);
  };

  // Handle team delete
  const handleTeamDelete = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;

    try {
      setLoading(true);
      const data = await authDelete(`/api/admin/teams/${teamId}`);

      if (data.success) {
        setSuccess('Team deleted successfully');
        loadTeams();
      } else {
        setError(data.message || 'Failed to delete team');
      }
    } catch (err) {
      console.error('Error deleting team:', err);
      setError('Failed to delete team');
    } finally {
      setLoading(false);
    }
  };

  // Handle sprint delete
  const handleSprintDelete = async (sprint) => {
    if (!window.confirm('Are you sure you want to delete this sprint?')) return;

    try {
      setLoading(true);
      const data = await authDelete(`/api/admin/sprints/${sprint.sprintName}`);

      if (data.success) {
        setSuccess('Sprint deleted successfully');
        loadSprints();
      } else {
        setError(data.message || 'Failed to delete sprint');
      }
    } catch (err) {
      console.error('Error deleting sprint:', err);
      setError('Failed to delete sprint');
    } finally {
      setLoading(false);
    }
  };

  // Handle sprint sync from ADO
  const handleSprintSync = async (sprint) => {
    const status = sprintStatuses[sprint.sprintName];
    if (status && status.sprintClosed) {
      setError('This sprint has been closed and cannot be synced.');
      return;
    }
    
    if (!window.confirm(`Sync sprint "${sprint.sprintName}" from Azure DevOps? This will fetch data for all teams.`)) return;

    try {
      setSyncingSprintId(sprint.sprintName);
      setError(null);
      sprintSyncStatusService.start(`Sprint data is being refreshed for "${sprint.sprintName}"…`);

      const data = await authPost('/api/sprint-report/sync-all-teams', {
        sprintName: sprint.sprintName,
        refreshWorkItemsFromAdo: true
      });

      if (data.success) {
        setSuccess(`Sprint "${sprint.sprintName}" synced successfully for all teams. Found ${data.totalWorkItems} work items.`);
        loadSprints(); // Reload to refresh statuses
      } else {
        setError(data.message || 'Failed to sync sprint data');
      }
    } catch (err) {
      console.error('Error syncing sprint:', err);
      setError(err.message || 'Failed to sync sprint data');
    } finally {
      setSyncingSprintId(null);
      sprintSyncStatusService.stop();
    }
  };


  // Handle close sprint
  const handleCloseSprint = async () => {
    if (!selectedSprint) return;

    try {
      setClosingSprintId(selectedSprint.sprintName);
      setError(null);

      const data = await authPost(`/api/sprint-report/close-sprint/${selectedSprint.sprintName}`);

      if (data.success) {
        setSuccess(data.message);
        setCloseDialogOpen(false);
        setSelectedSprint(null);
        loadSprints(); // Reload to refresh statuses
      } else {
        setError(data.message || 'Failed to close sprint');
      }
    } catch (err) {
      console.error('Error closing sprint:', err);
      setError(err.message || 'Failed to close sprint');
    } finally {
      setClosingSprintId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Sprint Management
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Note:</strong> Team management has been moved to the dedicated "Team Management" tab. 
          Use this section to manage sprints and sync data from Azure DevOps.
        </Typography>
      </Alert>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Sprint Configuration Settings */}
        {isAdmin() && (
          <Grid item xs={12}>
            <Card>
              <CardHeader
                title="Sprint Configuration"
              />
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Scope Change Calculation Days"
                      value={scopeChangeCalculationDays}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        // Allow 0; clamp to [0, 30]; treat empty/NaN as 0.
                        setScopeChangeCalculationDays(Number.isNaN(v) ? 0 : Math.max(0, Math.min(30, v)));
                      }}
                      inputProps={{ min: 0, max: 30, step: 1 }}
                      helperText="Number of days after sprint start to calculate scope changes. Items added/removed after this threshold are considered scope changes."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSaveConfig}
                      disabled={savingConfig}
                    >
                      {savingConfig ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Items added or removed after (sprint start + {scopeChangeCalculationDays} day{scopeChangeCalculationDays !== 1 ? 's' : ''}) will be marked as scope changes.
                </Typography>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Switch
                      id="new-sprint-kpi"
                      checked={newSprintKpiEnabled}
                      onChange={(e) => setNewSprintKpiEnabled(e.target.checked)}
                    />
                  }
                  label="New Sprint KPI"
                />
                <Typography variant="body2" color="textSecondary">
                  {newSprintKpiEnabled
                    ? 'Scrum Metrics shows the new layout: Quarterly Commitment and the new Sprint Review with leadership and team views (including the sprint work items), notes and a copy-ready sprint email.'
                    : 'Scrum Metrics keeps its current tabs and Sprint Review.'}
                  {' '}Click Save Configuration to apply.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Sprints Management */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title={
                <Box display="flex" alignItems="center">
                  <SprintIcon sx={{ mr: 1 }} />
                  Sprints Management
                </Box>
              }
              action={
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingSprint(null);
                    setSprintForm({ sprintName: '', startDate: '', endDate: '', quarter: '', year: '' });
                    setSprintDialogOpen(true);
                  }}
                >
                  Add Sprint
                </Button>
              }
            />
            <CardContent>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Sprint Name</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Quarter</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sprints.map((sprint) => {
                      const status = sprintStatuses[sprint.sprintName] || { sprintClosed: false };
                      return (
                        <TableRow key={sprint.sprintName}>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {sprint.sprintName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {sprint.startDate ? new Date(sprint.startDate).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {sprint.quarter} {sprint.year}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1} alignItems="center">
                              {status.sprintClosed && (
                                <Typography variant="caption" color="error.main" display="flex" alignItems="center" gap={0.5}>
                                  <LockIcon fontSize="small" />
                                  Closed
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleSprintSync(sprint)}
                              color="success"
                              disabled={syncingSprintId === sprint.sprintName || status.sprintClosed}
                              title={status.sprintClosed ? "Sprint is closed" : "Sync from ADO"}
                            >
                              <SyncIcon />
                            </IconButton>
                            {isAdmin() && (
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedSprint(sprint);
                                  setCloseDialogOpen(true);
                                }}
                                color="warning"
                                disabled={status.sprintClosed || closingSprintId === sprint.sprintName}
                                title="Close Sprint"
                              >
                                <LockIcon />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              onClick={() => handleSprintEdit(sprint)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleSprintDelete(sprint)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Team Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTeam ? 'Edit Team' : 'Add New Team'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Team ID"
              value={teamForm.id}
              onChange={(e) => setTeamForm({ ...teamForm, id: e.target.value })}
              margin="normal"
              required
              disabled={editingTeam} // Don't allow editing ID
              helperText="Unique identifier for the team (e.g., HQ-Core-SQ1)"
            />
            <TextField
              fullWidth
              label="Display Name"
              value={teamForm.displayName}
              onChange={(e) => setTeamForm({ ...teamForm, displayName: e.target.value })}
              margin="normal"
              required
              helperText="Human-readable team name"
            />
            <TextField
              fullWidth
              label="Description"
              value={teamForm.description}
              onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              helperText="Optional description of the team"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleTeamSubmit} variant="contained" disabled={loading}>
            {editingTeam ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close Sprint Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Close Sprint</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently close sprint "{selectedSprint?.sprintName}" for all teams. 
            Once closed, the sprint cannot be synced again. This action cannot be undone.
          </Alert>
          <Typography variant="body2">
            Closing a sprint prevents future data synchronization. All current data will be preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCloseSprint} 
            variant="contained" 
            color="error"
            disabled={closingSprintId !== null}
          >
            {closingSprintId ? 'Closing...' : 'Close Sprint'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sprint Dialog */}
      <Dialog open={sprintDialogOpen} onClose={() => setSprintDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSprint ? 'Edit Sprint' : 'Add New Sprint'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Sprint Name"
              value={sprintForm.sprintName}
              onChange={(e) => setSprintForm({ ...sprintForm, sprintName: e.target.value })}
              margin="normal"
              required
              helperText="Format: YYYY_SXX_MmmDD-MmmDD (e.g., 2025_S12_Jun04-Jun17)"
            />

            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={sprintForm.startDate}
              onChange={(e) => setSprintForm({ ...sprintForm, startDate: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={sprintForm.endDate}
              onChange={(e) => setSprintForm({ ...sprintForm, endDate: e.target.value })}
              margin="normal"
              required
              InputLabelProps={{ shrink: true }}
            />

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Quarter</InputLabel>
                  <Select
                    value={sprintForm.quarter}
                    label="Quarter"
                    onChange={(e) => setSprintForm({ ...sprintForm, quarter: e.target.value })}
                  >
                    <MenuItem value="Q1">Q1</MenuItem>
                    <MenuItem value="Q2">Q2</MenuItem>
                    <MenuItem value="Q3">Q3</MenuItem>
                    <MenuItem value="Q4">Q4</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Year"
                  value={sprintForm.year}
                  onChange={(e) => setSprintForm({ ...sprintForm, year: e.target.value })}
                  helperText="Auto-filled from start date"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSprintDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSprintSubmit} variant="contained" disabled={loading}>
            {editingSprint ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SprintManagement;
